/*
 * AB Jewellery — product page behaviour.
 * Variant selection re-renders only the affected regions through Shopify's
 * Section Rendering API, so price, availability, made-to-order status,
 * specifications and imagery always come from Shopify — never from guesses.
 */
(function () {
  'use strict';

  var AB = window.AB || {};
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function ProductSection(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.productUrl = root.getAttribute('data-product-url');
    this.updateUrl = root.getAttribute('data-update-url') === 'true';
    this.form = qs('form[data-ab-product-form]', root);
    try { this.variants = JSON.parse(qs('[data-ab-variants]', root).textContent); } catch (e) { this.variants = []; }
    this.controller = null;
    this.bind();
    this.initGallery();
    this.initSticky();
    this.initEngraving();
    this.initPanel();
  }

  /* Tall purchase panels stick by their bottom edge so nothing is ever clipped */
  ProductSection.prototype.initPanel = function () {
    var panel = qs('.product__panel', this.root);
    if (!panel || !('ResizeObserver' in window)) return;
    var update = function () {
      var headerOffset = 84;
      var top = Math.min(headerOffset, window.innerHeight - panel.offsetHeight - 24);
      panel.style.setProperty('--panel-top', top + 'px');
    };
    new ResizeObserver(update).observe(panel);
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  ProductSection.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('change', function (event) {
      if (event.target.matches('[data-ab-option]')) self.onOptionChange(event.target);
    });

    // Product quantity (not cart lines)
    this.root.addEventListener('click', function (event) {
      var btn = event.target.closest('.product__qty [data-ab-qty]');
      if (!btn) return;
      var input = btn.closest('[data-ab-quantity]').querySelector('input');
      var value = parseInt(input.value, 10) || 1;
      input.value = btn.getAttribute('data-ab-qty') === 'plus' ? value + 1 : Math.max(1, value - 1);
    });
  };

  ProductSection.prototype.selectedOptions = function () {
    var picker = qs('[data-ab-variant-picker]', this.root);
    if (!picker) return [];
    var values = [];
    qsa('[data-ab-option]', picker).forEach(function (input) {
      var index = parseInt(input.getAttribute('data-ab-option'), 10);
      if (input.tagName === 'SELECT' || input.checked) values[index] = input.value;
    });
    return values;
  };

  ProductSection.prototype.onOptionChange = function (input) {
    var self = this;
    var options = this.selectedOptions();

    // Update the visible "Metal: Yellow gold" label immediately
    var fieldset = input.closest('fieldset');
    if (fieldset) {
      var label = qs('[data-ab-selected-value]', fieldset);
      if (label) label.textContent = input.value;
    }

    var variant = this.variants.find(function (v) {
      return v.options.every(function (value, i) { return value === options[i]; });
    });

    if (!variant) {
      this.setUnavailable();
      return;
    }

    qs('[data-ab-variant-id]', this.root).value = variant.id;
    if (this.updateUrl && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    var focusedId = document.activeElement && document.activeElement.id;
    if (this.controller) this.controller.abort();
    this.controller = 'AbortController' in window ? new AbortController() : null;
    this.root.setAttribute('aria-busy', 'true');

    fetch(this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId, this.controller ? { signal: this.controller.signal } : {})
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = AB.parseHTML ? AB.parseHTML(html) : new DOMParser().parseFromString(html, 'text/html');
        self.swap(doc, variant);
        if (focusedId) {
          var el = document.getElementById(focusedId);
          if (el) el.focus({ preventScroll: true });
        }
        AB.announce && AB.announce(qs('[data-ab-swap="price"]', self.root).textContent.trim().replace(/\s+/g, ' '));
      })
      .catch(function () { /* aborted or offline: hidden input already holds the right variant */ })
      .finally(function () { self.root.removeAttribute('aria-busy'); });
  };

  ProductSection.prototype.swap = function (doc, variant) {
    var self = this;
    ['price', 'picker', 'mto', 'buy', 'accordions', 'sticky-price'].forEach(function (name) {
      var fresh = doc.querySelector('[data-ab-swap="' + name + '"]');
      var current = qs('[data-ab-swap="' + name + '"]', self.root);
      if (!fresh || !current) return;
      if (name === 'accordions') {
        // keep open/closed state of accordions
        var open = qsa('details', current).map(function (d) { return d.open; });
        current.innerHTML = fresh.innerHTML;
        qsa('details', current).forEach(function (d, i) { if (open[i]) d.open = true; });
      } else {
        current.innerHTML = fresh.innerHTML;
      }
    });

    // Gallery: re-render only when the variant has its own image
    if (variant.featured_media) {
      var gallery = qs('[data-ab-gallery]', this.root);
      if (gallery && String(gallery.getAttribute('data-first-media')) !== String(variant.featured_media)) {
        var freshGallery = doc.querySelector('[data-ab-swap="gallery"]');
        var currentGallery = qs('[data-ab-swap="gallery"]', this.root);
        if (freshGallery && currentGallery) {
          currentGallery.innerHTML = freshGallery.innerHTML;
          this.initGallery();
        }
      }
    }

    // Sticky bar button mirrors the main button
    var main = qs('[data-ab-add]', this.root);
    var sticky = qs('[data-ab-sticky-add]', this.root);
    if (main && sticky) {
      sticky.disabled = main.disabled;
      sticky.textContent = main.textContent.trim();
    }
  };

  ProductSection.prototype.setUnavailable = function () {
    var main = qs('[data-ab-add]', this.root);
    var sticky = qs('[data-ab-sticky-add]', this.root);
    [main, sticky].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = true;
      var label = btn.querySelector('span') || btn;
      label.textContent = AB.strings.unavailable;
    });
  };

  /* ---------- Gallery: counter, lightbox ---------- */
  ProductSection.prototype.initGallery = function () {
    var self = this;
    var track = qs('[data-ab-gallery-track]', this.root);
    var counter = qs('[data-ab-gallery-current]', this.root);
    if (track && counter) {
      var items = qsa('.gallery__item', track);
      var onScroll = function () {
        var width = track.clientWidth || 1;
        var index = Math.round(Math.abs(track.scrollLeft) / width);
        counter.textContent = Math.min(items.length, index + 1);
      };
      track.addEventListener('scroll', function () { window.requestAnimationFrame(onScroll); }, { passive: true });
    }

    qsa('[data-ab-zoom]', this.root).forEach(function (button) {
      button.addEventListener('click', function () { self.openLightbox(parseInt(button.getAttribute('data-ab-zoom'), 10), button); });
    });
  };

  ProductSection.prototype.openLightbox = function (index, opener) {
    var dialog = qs('[data-ab-lightbox]', this.root);
    if (!dialog || !AB.dialog) return;
    var trackEl = qs('[data-ab-lightbox-track]', dialog);
    trackEl.innerHTML = '';
    qsa('.gallery__zoom img', this.root).forEach(function (img) {
      var figure = document.createElement('figure');
      figure.className = 'lightbox__item';
      var big = img.cloneNode();
      big.removeAttribute('fetchpriority');
      big.setAttribute('sizes', '100vw');
      big.loading = 'lazy';
      figure.appendChild(big);
      trackEl.appendChild(figure);
    });
    AB.dialog.open(dialog, opener);
    var target = trackEl.children[index];
    if (target) window.setTimeout(function () { target.scrollIntoView({ block: 'start' }); }, 40);
  };

  /* ---------- Sticky Add to Bag (mobile & tablet) ---------- */
  ProductSection.prototype.initSticky = function () {
    var self = this;
    var bar = qs('[data-ab-sticky-atc]', this.root);
    var buy = qs('[data-ab-buy]', this.root);
    if (!bar || !buy || !('IntersectionObserver' in window)) return;
    var mq = window.matchMedia('(max-width: 989px)');

    var io = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      var show = passed && mq.matches;
      bar.hidden = !show;
      bar.setAttribute('aria-hidden', String(!show));
      var btn = qs('[data-ab-sticky-add]', bar);
      if (btn) btn.tabIndex = show ? 0 : -1;
      window.requestAnimationFrame(function () { bar.classList.toggle('is-visible', show); });
    });
    io.observe(qs('.product__buttons', buy) || buy);

    bar.addEventListener('click', function (event) {
      if (!event.target.closest('[data-ab-sticky-add]')) return;
      var main = qs('[data-ab-add]', self.root);
      if (!self.form || !main) return;
      if (typeof self.form.requestSubmit === 'function') self.form.requestSubmit(main);
      else main.click();
    });
  };

  /* ---------- Engraving: reveal, count, validate ---------- */
  ProductSection.prototype.initEngraving = function () {
    var self = this;
    this.root.addEventListener('change', function (event) {
      var toggle = event.target.closest('[data-ab-engraving-toggle]');
      if (!toggle) return;
      var wrap = toggle.closest('[data-ab-engraving]');
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', String(toggle.checked));
      if (panel) panel.hidden = !toggle.checked;
      qsa('[data-ab-engraving-input]', wrap).forEach(function (input) { input.disabled = !toggle.checked; });
      if (toggle.checked) {
        var first = qs('input[data-ab-engraving-input]', wrap);
        if (first) first.focus();
      }
    });

    this.root.addEventListener('input', function (event) {
      var input = event.target.closest('input[data-ab-engraving-input]');
      if (!input) return;
      var wrap = input.closest('[data-ab-engraving]');
      var count = qs('[data-ab-engraving-count]', wrap);
      if (count) count.textContent = input.value.length;
      self.clearEngravingError(input);
    });

    if (!this.form) return;
    // Capture phase: runs before the global Ajax add-to-bag handler
    this.form.addEventListener('submit', function (event) {
      var wrap = qs('[data-ab-engraving]', self.root);
      if (!wrap) return;
      var toggle = qs('[data-ab-engraving-toggle]', wrap);
      var input = qs('input[data-ab-engraving-input]', wrap);
      if (!toggle || !toggle.checked || !input) return;
      var limit = parseInt(wrap.getAttribute('data-limit'), 10) || 0;
      var value = input.value.trim();
      var message = '';
      if (!value) message = AB.strings.engravingRequired;
      else if (limit && value.length > limit) message = AB.strings.engravingTooLong;
      if (message) {
        event.preventDefault();
        event.stopImmediatePropagation();
        self.showEngravingError(input, message);
      }
    }, true);
  };

  ProductSection.prototype.showEngravingError = function (input, message) {
    var error = document.getElementById(input.id + '-error');
    input.setAttribute('aria-invalid', 'true');
    if (error) { error.hidden = false; qs('span', error).textContent = message; }
    input.focus();
  };

  ProductSection.prototype.clearEngravingError = function (input) {
    var error = document.getElementById(input.id + '-error');
    input.removeAttribute('aria-invalid');
    if (error) error.hidden = true;
  };

  /* ---------- Recommendations (lazy) ---------- */
  function initRecommendations(root) {
    var url = root.getAttribute('data-url');
    if (!url || root.dataset.loaded) return;
    var load = function () {
      root.dataset.loaded = 'true';
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-ab-recommendations]');
        if (fresh && fresh.querySelector('.card')) {
          root.innerHTML = fresh.innerHTML;
          root.hidden = false;
          if (AB.wishlist) AB.wishlist.sync();
          if (AB.initReveal) AB.initReveal();
        }
      }).catch(function () {});
    };
    if (!('IntersectionObserver' in window)) return load();
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { io.disconnect(); load(); }
    }, { rootMargin: '0px 0px 400px 0px' });
    io.observe(root);
  }

  function boot() {
    qsa('[data-ab-product]').forEach(function (root) {
      if (root.dataset.abInit) return;
      root.dataset.abInit = 'true';
      new ProductSection(root);
    });
    qsa('[data-ab-recommendations][data-url]').forEach(initRecommendations);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
